import { useState } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeftIcon, Trash2Icon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { PageHeader } from '@ems/ui/patterns/page-header'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { hasPlatformScope } from '#admin/features/platform/api.ts'
import { useDeletePlan } from '#admin/features/platform/plans/mutations.ts'
import { PlanEditionsCard } from '#admin/features/platform/plans/plan-editions-card.tsx'
import { PlanEditor } from '#admin/features/platform/plans/plan-editor.tsx'
import { planQuery } from '#admin/features/platform/plans/queries.ts'
import { toastError } from '#admin/lib/toast-error.ts'
import { PlanSkeleton } from '#admin/features/platform/platform-skeletons.tsx'

/** `/platform/plans/$planId` — edit a plan, and see the editions it reaches. */
export const Route = createFileRoute('/_authenticated/platform/plans/$planId')({
  pendingComponent: PlanSkeleton,
  staticData: { crumb: usePlanCrumb },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(planQuery(params.planId)),
  notFoundComponent: NotFoundView,
  component: PlanPage,
})

function usePlanCrumb(): string {
  const { planId } = Route.useParams()
  return useQuery(planQuery(planId)).data?.name ?? 'Plan'
}

function PlanPage() {
  const { planId } = Route.useParams()
  const { data: plan } = useSuspenseQuery(planQuery(planId))
  const { data: session } = useSuspenseQuery(sessionQuery())
  const navigate = useNavigate()
  const remove = useDeletePlan()
  const [confirming, setConfirming] = useState(false)
  const canManage = hasPlatformScope(session, 'platform:plans.manage')

  return (
    <>
      <div className="flex flex-col gap-4">
        <Link
          to="/platform/plans"
          className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
        >
          <ArrowLeftIcon className="size-4" />
          Plans
        </Link>
        <PageHeader
          title={plan.name}
          description={
            <>
              <span className="font-mono">{plan.key}</span> · {plan.tenantCount} edition
              {plan.tenantCount === 1 ? '' : 's'}
              {plan.isActive ? '' : ' · not offered to new editions'}
            </>
          }
          actions={
            canManage ? (
              <Button
                variant="outline"
                onClick={() => setConfirming(true)}
                disabled={plan.tenantCount > 0}
                title={
                  plan.tenantCount > 0 ? 'Move its editions to another plan first.' : undefined
                }
              >
                <Trash2Icon />
                Delete plan
              </Button>
            ) : null
          }
        />
      </div>

      <PlanEditor
        // Remounted when the saved plan changes, so the form starts again from what was stored.
        key={JSON.stringify([
          plan.name,
          plan.description,
          plan.isActive,
          plan.quotas,
          plan.modules,
        ])}
        plan={plan}
        canManage={canManage}
        beforeModules={<PlanEditionsCard plan={plan} />}
      />

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Delete the ${plan.name} plan?`}
        description="No edition is on it, so nothing changes for anybody. Its key stays reserved in the audit trail."
        confirmLabel="Delete plan"
        destructive
        busy={remove.isPending}
        onConfirm={() =>
          remove.mutate(plan.id, {
            onSuccess: () => {
              setConfirming(false)
              void navigate({ to: '/platform/plans' })
            },
            onError: (error) => {
              setConfirming(false)
              toastError(error)
            },
          })
        }
      />
    </>
  )
}
